import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { BrandMark } from '../components/BrandMark';
import { authenticateBrowser, type LoginWidgetPayload } from '../lib/auth';
import { BOT_URL, BOT_USERNAME } from './shared';

/**
 * Browser-cabinet sign-in. Renders the official Telegram Login Widget
 * and exchanges its signed payload for a session via
 * `/v1/auth/telegram-login`. On success: an onboarded account goes
 * straight to the cabinet; a not-yet-onboarded one is told to finish
 * setup in the Telegram bot (wallet creation stays in TG — see
 * account-model decision).
 */
export function CabinetLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const container = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    (
      window as unknown as { onTelegramAuth?: (u: LoginWidgetPayload) => void }
    ).onTelegramAuth = (user) => {
      setError('');
      setBusy(true);
      void (async () => {
        try {
          const resp = await authenticateBrowser(user);
          // wallet_address is the server-truth onboarding signal — it's
          // set once the user's EOA/deposit wallet exists. localStorage
          // `isOnboarded()` can't be trusted here (fresh browser/device).
          if (!resp.user.wallet_address) {
            setNeedsOnboarding(true);
            setBusy(false);
            return;
          }
          navigate('/app', { replace: true });
        } catch (e) {
          setError(e instanceof Error ? e.message : t('cabinet.login_failed'));
          setBusy(false);
        }
      })();
    };

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', BOT_USERNAME);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-userpic', 'true');
    container.current?.appendChild(script);

    return () => {
      (window as unknown as { onTelegramAuth?: unknown }).onTelegramAuth =
        undefined;
    };
  }, [navigate, t]);

  return (
    <div
      data-theme="dark"
      className="flex min-h-screen flex-col items-center justify-center bg-tg-bg px-4 text-tg-text"
    >
      <div className="w-full max-w-sm rounded-pb-lg border border-pb-border-soft bg-pb-paper/60 p-6 text-center backdrop-blur-md">
        <div className="mb-5 flex flex-col items-center gap-3">
          <BrandMark size="md" />
          {needsOnboarding ? (
            <>
              <h1 className="pb-display text-[20px]">
                {t('cabinet.onboard_title')}
              </h1>
              <p className="max-w-xs text-sm text-tg-hint">
                {t('cabinet.onboard_body')}
              </p>
            </>
          ) : (
            <>
              <h1 className="pb-display text-[20px]">
                {t('cabinet.login_title')}
              </h1>
              <p className="max-w-xs text-sm text-tg-hint">
                {t('cabinet.login_subtitle')}
              </p>
            </>
          )}
        </div>

        {needsOnboarding ? (
          <a
            href={BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="pb-btn pb-btn-primary pb-focus-ring w-full justify-center"
          >
            {t('cabinet.open_bot')}
          </a>
        ) : (
          <div
            ref={container}
            className="flex min-h-[42px] items-center justify-center"
          />
        )}

        {busy ? (
          <div className="mt-3 text-sm text-tg-hint">
            {t('cabinet.login_busy')}
          </div>
        ) : null}
        {error ? (
          <div className="mt-3 rounded-pb-md border border-pb-danger/30 bg-pb-danger/10 px-3 py-2 text-sm text-pb-danger">
            {error}
          </div>
        ) : null}

        <Link
          to="/"
          className="pb-focus-ring mt-5 inline-block text-xs text-tg-hint hover:text-tg-text"
        >
          {t('cabinet.back_to_site')}
        </Link>
      </div>
    </div>
  );
}
